import { forwardRef } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { NumericFormat } from 'react-number-format';

import { useFieldContext } from '~/common/form/formContexts';

import Input from './InputBase/Input';

type NumericFormatProps = React.ComponentProps<typeof NumericFormat>;
type MuiInputType = Omit<
  React.ComponentProps<typeof Input>,
  'helperText' | 'defaultValue' | 'onChange'
>;

type NumberInputProps = Partial<MuiInputType> &
  Omit<Partial<NumericFormatProps>, 'onChange'> & {
    onChange?: NumericFormatProps['onValueChange'];
    min?: number;
    max?: number;
    errorMessage?: string;
    defaultValue?: string | number;
    useReactHooksForm?: boolean;
    useTanstackForm?: boolean;
  };

const NumberInput = forwardRef<
  React.ElementRef<typeof Input>,
  Readonly<NumberInputProps>
>(({ useReactHooksForm = false, useTanstackForm = false, ...props }, ref) => {
  if (useReactHooksForm) {
    return <HookFormNumberInput ref={ref} {...props} />;
  }
  if (useTanstackForm) {
    return <TanstackFormNumberInput ref={ref} {...props} />;
  }
  return <BaseNumberInput {...props} ref={ref} />;
});

function handleFloatValueChange(onChange: (value: number | undefined) => void) {
  return ({ floatValue }: { floatValue: number | undefined }) =>
    onChange(floatValue);
}

const HookFormNumberInput = forwardRef<
  React.ElementRef<typeof Input>,
  Readonly<NumberInputProps>
>(({ name, ...props }, ref) => {
  const {
    control,
    formState: { errors },
  } = useFormContext();

  return (
    <Controller
      name={name}
      control={control}
      render={({ onChange, ...hooksProps }, { invalid }) => (
        <BaseNumberInput
          error={invalid}
          errorMessage={errors[name]?.message}
          {...props}
          {...hooksProps}
          ref={ref}
          onChange={handleFloatValueChange(onChange)}
        />
      )}
    />
  );
});

const BaseNumberInput = forwardRef<
  React.ElementRef<typeof Input>,
  Readonly<NumberInputProps>
>(
  (
    {
      min = Number.MIN_SAFE_INTEGER,
      max = Number.MAX_SAFE_INTEGER,
      onFocus,
      InputProps,
      maxLength,
      onChange,
      ...props
    },
    ref,
  ) => {
    return (
      <NumericFormat<MuiInputType>
        getInputRef={ref}
        customInput={Input}
        isAllowed={({ floatValue, value }) => {
          if (floatValue === undefined) return true;
          return (
            floatValue <= max && floatValue >= min && value.length <= maxLength
          );
        }}
        InputProps={{
          onFocus,
          ...InputProps,
        }}
        onValueChange={onChange}
        {...props}
      />
    );
  },
);

const TanstackFormNumberInput = forwardRef<
  React.ElementRef<typeof Input>,
  Readonly<NumberInputProps>
>((props, ref) => {
  const field = useFieldContext<number | undefined>();
  const errorMessage = field.state.meta.errors[0] as string | undefined;
  return (
    <BaseNumberInput
      {...props}
      ref={ref}
      value={field.state.value as any}
      error={field.state.meta.errors.length > 0}
      errorMessage={errorMessage}
      onChange={handleFloatValueChange((v) => field.handleChange(v))}
      onBlur={field.handleBlur}
    />
  );
});

export default NumberInput;
